/**
 * Home / sign-in screen: pick your avatar, then see your books.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useSession } from "@/app/useSession";
import { books as booksApi, READING_LEVELS, type BookSummary } from "@/lib/api";

/** Ladder position for ordering level groups; unknown levels sort to the end. */
function levelRank(level: string): number {
  const index = (READING_LEVELS as readonly string[]).indexOf(level);
  return index === -1 ? READING_LEVELS.length : index;
}

interface LevelGroup {
  level: string;
  books: BookSummary[];
}

/**
 * Group a flat book list into per-level buckets ordered along the RAZ ladder
 * (lowest band first), so a child sees their books shelved by reading level.
 */
function groupByLevel(bookList: BookSummary[]): LevelGroup[] {
  const byLevel = new Map<string, BookSummary[]>();
  for (const book of bookList) {
    const bucket = byLevel.get(book.level);
    if (bucket) bucket.push(book);
    else byLevel.set(book.level, [book]);
  }
  return [...byLevel.entries()]
    .map(([level, books]) => ({ level, books }))
    .sort((a, b) => levelRank(a.level) - levelRank(b.level));
}

export function HomePage() {
  const { childrenList, activeChild, selectChild, loading } = useSession();
  const navigate = useNavigate();
  const [bookList, setBookList] = useState<BookSummary[]>([]);

  useEffect(() => {
    if (!activeChild) return;
    let active = true;
    booksApi
      .list(activeChild.id)
      .then((list) => {
        if (active) setBookList(list);
      })
      .catch(() => {
        if (active) setBookList([]);
      });
    return () => {
      active = false;
    };
  }, [activeChild]);

  const levelGroups = useMemo(() => groupByLevel(bookList), [bookList]);


  if (loading) {
    return <p role="status">Loading…</p>;
  }

  if (!activeChild) {
    return (
      <section aria-label="Choose your avatar">
        <h1 style={{ fontSize: "2rem" }}>Who's reading today?</h1>
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(8rem, 1fr))",
            gap: "1rem",
          }}
        >
          {childrenList.map((child) => (
            <li key={child.id}>
              <button
                type="button"
                className="kid-button kid-button--secondary"
                style={{ flexDirection: "column", width: "100%", minHeight: "8rem" }}
                onClick={() => void selectChild(child.id)}
              >
                <span aria-hidden="true" style={{ fontSize: "3rem" }}>
                  {child.avatar || "🙂"}
                </span>
                <span>{child.displayName}</span>
              </button>
            </li>
          ))}
          {childrenList.length === 0 && (
            <li>
              <p>Ask a grown-up to set up your profile.</p>
            </li>
          )}
        </ul>
      </section>
    );
  }

  return (
    <section aria-label="Your books">
      <h1 style={{ fontSize: "2rem" }}>Hi {activeChild.displayName}! 👋</h1>
      <button
        type="button"
        className="kid-button kid-button--primary"
        style={{ fontSize: "1.5rem", marginBottom: "1.5rem" }}
        onClick={() => navigate("/create")}
      >
        ✨ Make a new book
      </button>

      <h2 style={{ fontSize: "1.5rem" }}>Your books</h2>
      {bookList.length === 0 ? (
        <p>No books yet — make your first one!</p>
      ) : (
        levelGroups.map((group) => (
          <details
            key={group.level}
            // Open the shelf for the child's current reading level by default so
            // their most relevant books are visible without a tap; other levels
            // stay tidied away and can be opened on demand.
            open={group.level === activeChild.readingLevel}
            className="kid-card"
            style={{ padding: "0.75rem 1rem", marginBottom: "1rem" }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontSize: "1.25rem",
                fontWeight: 700,
                listStyle: "revert",
              }}
            >
              Level {group.level}{" "}
              <span style={{ color: "var(--color-slate)", fontWeight: 400 }}>
                ({group.books.length}{" "}
                {group.books.length === 1 ? "book" : "books"})
              </span>
            </summary>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                marginTop: "0.75rem",
                marginBottom: 0,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))",
                gap: "1rem",
              }}
            >
              {group.books.map((book) => (
                <li
                  key={book.id}
                  className="kid-card"
                  style={{ padding: "0.75rem" }}
                >
                  <Link
                    to={`/book/${book.id}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    {book.coverUrl && (
                      <img
                        src={book.coverUrl}
                        alt=""
                        style={{ width: "100%", borderRadius: "0.75rem" }}
                      />
                    )}
                    <p style={{ fontWeight: 700 }}>{book.title}</p>
                    <p style={{ color: "var(--color-slate)" }}>
                      Level {book.level}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </details>
        ))
      )}
    </section>
  );
}
